/**
 * Configuration for Data Mode toggle availability per screen.
 * 
 * Key format: For top-level screens use the screen name (e.g., "Customers").
 * For sub-menu screens, concatenate parent and child with underscore (e.g., "Settings_Company").
 * 
 * Value: true = toggle enabled (can switch between Demo/Live), false = toggle disabled (Demo only)
 */
export const screenModeConfig: Record<string, boolean> = {
  // Top-level screens
  Homepage: false,
  Customers: true,
  Transactions: true,
  Payments: false,
  Credit_Scoring: false,

  // Collections sub-screens
  Collections_Dashboard: false,
  Collections_Collections_Queue: false,
  Collections_Promise_to_Pay: false,
  Collections_Ageing_Analysis: false,

  // Dispute Management sub-screens
  Dispute_Management_Dashboard: false,
  Dispute_Management_All_Disputes: false,

  // Settings sub-screens
  Settings_Company: true,
  Settings_Users: true,
  Settings_Integrations: false,
  Settings_Templates: false,
  Settings_Collection_Strategies: false,
  Settings_Dispute_Types: false,
  Settings_AI_Configuration: false,
};

/**
 * Get the screen key from a pathname.
 * Maps URL paths to the screen configuration keys.
 */
export function getScreenKeyFromPath(pathname: string): string | null {
  const pathToKeyMap: Record<string, string> = {
    '/': 'Homepage',
    '/customers': 'Customers',
    '/transactions': 'Transactions',
    '/payments': 'Payments',
    '/credit-scoring': 'Credit_Scoring',

    // Collections
    '/collections/dashboard': 'Collections_Dashboard',
    '/collections/queue': 'Collections_Collections_Queue',
    '/collections/promises': 'Collections_Promise_to_Pay',
    '/collections/ageing-analysis': 'Collections_Ageing_Analysis',

    // Dispute Management
    '/disputes': 'Dispute_Management_Dashboard',
    '/disputes/all': 'Dispute_Management_All_Disputes',

    // Settings
    '/settings/company': 'Settings_Company',
    '/users': 'Settings_Users',
    '/integrations': 'Settings_Integrations',
    '/settings/templates': 'Settings_Templates',
    '/settings/strategies': 'Settings_Collection_Strategies',
    '/settings/dispute-types': 'Settings_Dispute_Types',
    '/settings/ai-configuration': 'Settings_AI_Configuration',
  };

  // First try exact match
  if (pathToKeyMap[pathname]) {
    return pathToKeyMap[pathname];
  }

  // For customer detail pages like /customers/123, return Customers
  if (pathname.startsWith('/customers/')) {
    return 'Customers';
  }

  return null;
}

/**
 * Check if the Data Mode toggle should be enabled for a given pathname.
 * Returns true if toggle should be shown, false if only "Demo" label should be shown.
 */
export function isToggleEnabledForPath(pathname: string): boolean {
  const screenKey = getScreenKeyFromPath(pathname);
  if (screenKey === null) {
    // Unknown screen, default to disabled (Demo only)
    return false;
  }
  return screenModeConfig[screenKey] ?? false;
}
